import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';

import ToggleOption from '@/components/common/ToggleOption/ToggleOption';
import AgConfirmationModal from '@/components/common/ModalWindow/AgConfirmationModal';
import useGetContractDetailsFromCache from '@/hooks/contract/useGetContractDetailsFromCache.hook';
import useGetContractLinesFromCache from '@/hooks/contract/useGetContractLinesFromCache.hook';
import { ContractBillingStatusPascalCaseEnum } from '@/graphql/contracts/_generated_/contracts';
import CustomToggleTitleComponent from './components/CustomToggleTitleComponent';
import useUpdateContractRenewal from './hooks/useUpdateContractRenewal';

export default function DaysBeforeRenewalContainer() {
  const { contractId }: { contractId: UUID } = useParams();
  const contract = useGetContractDetailsFromCache(contractId);
  const daysBeforeRenewalEqualZero = contract?.daysBeforeRenewal === 0;
  const [isEnabled, setIsEnabled] = useState<boolean>(
    !daysBeforeRenewalEqualZero ? Boolean(contract?.daysBeforeRenewal) : true
  );
  const [isVisibleForm, setIsVisibleForm] = useState<boolean>(false);
  const [showDeactivateConfirmation, setShowDeactivateConfirmation] = useState<boolean>(false);
  const {
    isLoadingUpdateContractRenewal,
    errorUpdateContractRenewal,
    updateContractRenewal,
  } = useUpdateContractRenewal(contractId);

  const { cachedVariableContractLines, cachedBCContractLines } = useGetContractLinesFromCache(contractId);

  const hasAnyLinePaused = useMemo(() => {
    const bcLines = cachedBCContractLines?.paginatedFilteredContractLinesByBillingCycles?.items || [];
    const variableLines = cachedVariableContractLines?.paginatedFilteredVariableContractLines?.items || [];

    const bcLinesPaused = bcLines.some(line =>
      line?.lineBillingStatus === ContractBillingStatusPascalCaseEnum.Paused ||
      line?.lineBillingStatus === ContractBillingStatusPascalCaseEnum.FuturePause
    );

    const variableLinesPaused = variableLines.some(line =>
      line?.lineBillingStatus === ContractBillingStatusPascalCaseEnum.Paused ||
      line?.lineBillingStatus === ContractBillingStatusPascalCaseEnum.FuturePause
    );

    return bcLinesPaused || variableLinesPaused;
  }, [cachedBCContractLines, cachedVariableContractLines]);

  const hasPauseActive = hasAnyLinePaused;

  const isVisible = contract;

  const onChangeToggle = () => {
    if (isEnabled && (contract?.daysBeforeRenewal || daysBeforeRenewalEqualZero)) {
      setShowDeactivateConfirmation(true);
    } else {
      setIsEnabled(!isEnabled);
      setIsVisibleForm(true);
    }
  };

  const onConfirmDeactivate = () => {
    setIsEnabled(false);
    updateContractRenewal(null);
  };

  useEffect(() => {
    if (isEnabled && !contract?.daysBeforeRenewal && errorUpdateContractRenewal) {
      setIsEnabled(false);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [errorUpdateContractRenewal]);

  useEffect(() => {
    if (!contract?.daysBeforeRenewal && !daysBeforeRenewalEqualZero) {
      setIsEnabled(false);
    }
  }, [contract?.daysBeforeRenewal, daysBeforeRenewalEqualZero]);

  if (!isVisible) {
    return null;
  }

  return (
    <>
      <ToggleOption
        id="days-before-renewal"
        isEnable={isEnabled}
        onChange={onChangeToggle}
        title={(
          <CustomToggleTitleComponent
            title="Days before renewal to raise SO"
            isEnabled={isEnabled}
            isVisibleForm={isVisibleForm}
            setIsVisibleForm={setIsVisibleForm}
            disableToggle={() => setIsEnabled(false)}
            handleSubmit={updateContractRenewal}
            days={contract?.daysBeforeRenewal}
            isLoading={isLoadingUpdateContractRenewal}
          />
        )}
      />
      <AgConfirmationModal
        isOpen={showDeactivateConfirmation}
        title="You are about to modify Days Before Renewal for this contract"
        onClose={() => setShowDeactivateConfirmation(false)}
        onConfirm={onConfirmDeactivate}
        contentStyle={{ width: 600 }}
      >
        You are about to modify Days Before Renewal for this contract.<br />
        This change could potentially impact Sales Order generation. <br />
        {hasPauseActive && (
          <>
            <br />
            <strong>Warning:</strong> This action will change the billing date.
            A Pause to billing is set. Please review your Pause End Date to ensure accurate billing.<br />
          </>
        )}
        Please confirm you wish to continue.
      </AgConfirmationModal>
    </>
  );
}
